import type { Temporada } from '../../utils/temporada';
import { baseApi } from './baseApi';
import type { PaginationMeta } from '../../types';

import type { CurrencyCode } from '../../utils/moneda';
/** Rótulo quemado sobre la foto (ver features/properties/portadaTexto). */
export interface PropertyImageOverlay {
  eyebrow?: string;
  title?: string;
  xPct: number;
  yPct: number;
  sizePct: number;
  color?: string;
  /** Banda difuminada de lado a lado atrás del texto (opcional). */
  banda?: boolean;
}

export interface PropertyImage {
  _id?: string;
  url: string;
  thumbnail: string;
  order: number;
  isMain: boolean;
  caption?: string;
  /** La foto SIN el rótulo: es de donde se recompone al volver a editarlo. */
  originalUrl?: string;
  overlay?: PropertyImageOverlay;
}

// Resumen liviano que devuelve GET /properties/:id/card (network-wide, campos seguros)
export interface PropertyCardSummary {
  propertyId: string;
  title: string;
  type: string;
  operation: 'sale' | 'rent' | 'both' | 'temporal_rent';
  status: 'draft' | 'active' | 'reserved' | 'sold' | 'rented' | 'inactive';
  pricing: { salePrice: number | null; rentPrice: number | null; currency: CurrencyCode };
  location: { address: string | null; neighborhood: string | null; city: string | null; state: string | null; buildingName?: string | null; padron?: string | null };
  features: { bedrooms: number | null; bathrooms: number | null; totalArea: number | null; coveredArea?: number | null };
  media: { mainImage: string | null };
  agent: string | null;
  /** true si la propiedad es de otra oficina/tenant (bolsa): la ficha propia la bloquea,
   *  hay que abrirla por /bolsa-propiedades/:code. */
  isPool?: boolean;
}

/** Escribano de una de las partes de la reserva. Contacto suelto (puede ser externo). */
export interface NotaryContact {
  firstName?: string;
  lastName?:  string;
  phone?:     string;
  email?:     string;
}

// Resultado del matcheo por pedido (feature Pedidos): propiedad que calza con un
// interés del cliente, con score/motivos y flag si viene de la bolsa.
export interface InterestMatch {
  propertyId:   string;
  title:        string;
  type:         string;
  operation:    string;
  price:        number | null;
  currency:     string;
  bedrooms:     number | null;
  bathrooms:    number | null;
  totalArea:    number | null;
  neighborhood: string | null;
  city:         string | null;
  lat:          number | null;
  lng:          number | null;
  distance?:    number | null;
  mainImage:    string | null;
  agent:        string | null;
  fromPool:     boolean;
  /** Publicación de Captación externa (scrape de portales) — navega a su propio detalle. */
  fromExternal?: boolean;
  externalId?:   string;
  score:        number;
  reasons:      string[];
}

export interface Property {
  _id: string;
  propertyId: string;
  office: string;
  status: 'draft' | 'active' | 'reserved' | 'sold' | 'rented' | 'inactive';
  temperature?: 'opportunity' | 'cold' | 'warm' | 'hot' | 'negotiating' | null;
  /** Temperatura efectiva resuelta por el backend (modo auto → según actividad/antigüedad).
   *  Se MUESTRA esta; el selector edita `temperature`. Viene en el detalle y en el listado. */
  effectiveTemperature?: 'opportunity' | 'cold' | 'warm' | 'hot' | 'negotiating';
  sharedInPool?: boolean;
  operation: 'sale' | 'rent' | 'both' | 'temporal_rent';
  title: string;
  description: string;
  privateDescription?: string;
  type: 'apartment' | 'house' | 'commercial' | 'land' | 'garage' | 'other'
      | 'farm' | 'field' | 'warehouse' | 'room' | 'business' | 'office';
  subCategory?: string;
  pricing: {
    salePrice?: number;
    rentPrice?: number;
    /** Alquiler temporal: precio por día (el que va a ML Temporada). */
    dailyPrice?: number;
    /** Alquiler temporal: precio por quincena (15 días). */
    fortnightPrice?: number;
    currency: CurrencyCode;
    isNegotiable: boolean;
    /** "A consultar": el precio no se publica. El backend lo saca al servir. */
    priceOnRequest?: boolean;
    includesExpenses: boolean;
    monthlyExpenses?: number;
    expensesCurrency?: CurrencyCode;
  };
  /** Alquiler temporal: disponibilidad y precio por fecha (ver utils/temporada.ts). */
  temporada?: Temporada;
  exclusive?: boolean | null; // null/undefined = sin definir
  location: {
    address: string;
    city: string;
    /** Code estable de la ciudad (slug del catálogo central). */
    cityCode?: string;
    state?: string;
    country: string;
    neighborhood?: string;
    /** Code estable del barrio. */
    neighborhoodCode?: string;
    postalCode?: string;
    coordinates?: { lat: number; lng: number };
    isExactAddress: boolean;
    /** Nombre del edificio — predictor clave para apartamentos. */
    buildingName?: string;
    /** Padrón catastral (opcional) — mapa de cierre por padrón / tasaciones. */
    padron?: string;
    /** Dirección estructurada que el padrón no devuelve (se capta al reservar). */
    doorNumber?: string;
    apartmentNumber?: string;
    corner?: string;
    /** Distancias cacheadas vía OSM, en metros. */
    distances?: {
      beach?:       number;
      mainAvenue?:  number;
      supermarket?: number;
      school?:      number;
      plaza?:       number;
      enrichedAt?:  string;
    };
  };
  features: {
    totalArea?: number;
    /** Unidad de `totalArea`. Ausente = m² (lo cargado antes de que existiera la opción). */
    areaUnit?: 'm2' | 'ha';
    /** La misma superficie en m². Lo calcula el backend; es lo que comparan filtros,
     *  tasación y los portales. `totalArea` + `areaUnit` son para mostrar. */
    totalAreaM2?: number;
    coveredArea?: number;
    bedrooms?: number;
    bathrooms?: number;
    garages?: number;
    floor?: number;
    totalFloors?: number;
    yearBuilt?: number;
    orientation?: string;
    amenities: string[];
    condition: 'new' | 'excellent' | 'good' | 'needs_work';
  };
  /** Garantías de alquiler que la propiedad acepta. Vacío = sin informar. */
  rentGuarantees?: string[];
  /** Acepta financiación bancaria en la venta. undefined = sin informar. */
  acceptsBankFinancing?: boolean;
  media: {
    images: PropertyImage[];
    videos: Array<{ url: string; type: 'upload' | 'youtube' | 'vimeo' }>;
    virtualTour?: string;
    floorPlan?: string;
  };
  owner?: {
    customerId?: string;
    name: string;
    phone?: string;
    email?: string;
  };
  assignedTo: string;
  /** `avatar` y `phone` los resuelve el backend al vuelo desde el User, no están persistidos. */
  assignedToSnapshot: { name: string; lastName: string; avatar?: string; phone?: string };
  /** Estado del agente asignado: true=activo, false=desactivado, null=desconocido. */
  assignedToActive?: boolean | null;
  /**
   * ¿El asesor asignado puede ATENDER lo que le mande el Agente Virtual?
   *
   * No es lo mismo que `assignedToActive`, que solo dice si la cuenta está habilitada:
   * suma el caso del que nunca accedió al CRM. Lo resuelve el backend con el criterio
   * único (`atencionDelAsesor.ts`). `null` = no hay asesor del que decir nada.
   */
  asesorAtiende?: boolean | null;
  /** Texto de la pastilla cuando no atiende ("Asesor inactivo", "Sin acceso aún"). */
  asesorMotivo?: string | null;
  /** Co-listing: propiedad trabajada en conjunto con otro agente (mismo office).
   *  splitPct es el % del co-agente; el principal se lleva 100 - splitPct. */
  coListing?: {
    agentId: string;
    agentSnapshot: { name: string; lastName: string; avatar?: string };
    splitPct: number;
    addedBy?: string;
    addedAt?: string;
  } | null;
  /** Teléfono del agente — lo agrega el endpoint público (getProperty) para el botón de WhatsApp. */
  agentPhone?: string;
  /** Foto del agente — la agrega el endpoint público (getProperty) para la ficha "Agente responsable". */
  agentAvatar?: string;
  /** Slug de "Mi página" del asesor a cargo — lo manda el endpoint público SOLO si la página
   *  está online. Sin esto, el asesor se muestra pero no lleva a ningún lado. */
  agentPortfolioSlug?: string;
  marketing: {
    publishOnWeb: boolean;
    publishOnMercadoLibre: boolean;
    publishOnGallito?: boolean;
    publishOnInfocasas?: boolean;
    aiPhotos?: boolean;
    infocasasLastIncludedAt?: string;
    featured: boolean;
    tags: string[];
    meliId?: string;
    meliUrl?: string;
    meliListingType?: string;
    meliCategoryId?: string;
    meliSyncAt?: string;
    /** Cuándo empezó a estar publicado el aviso ACTUAL en ML. */
    meliPublishedAt?: string;
    /** Última republicación (cierra el aviso y abre uno nuevo). */
    meliRepublishedAt?: string;
    meliSyncError?: string;
    /** Fotos que ML no acepta por medir menos de 500×500 y quedaron afuera del aviso. */
    meliPhotoNotice?: string;
    /**
     * El teléfono del aviso de ML es el del Agente Virtual: él atiende las consultas.
     *
     * Lo prende el asesor propiedad por propiedad desde la ficha. NO es lo mismo que el
     * sync de la cartera hacia Phylax — ese empuja todo para que el Agente pueda hablar
     * de las propiedades, pero no lo pone a atender el aviso de ninguna. (17/08/2026)
     */
    meliGustavoContact?: boolean;
    gallitoId?: string;
  };
  statusHistory: Array<{
    from: string;
    to: string;
    changedBy: string;
    changedAt: string;
    note?: string;
  }>;
  /** Datos del cierre de la operación. */
  closing?: {
    closedPrice:   number;
    currency:      CurrencyCode;
    closedAt:      string;
    closedBy?:     string;
    daysOnMarket?: number;
    notes?:        string;
  };

  /** Análisis CV cacheado. */
  imageAnalysis?: {
    conditionScore:    number;
    inferredCondition: 'new' | 'excellent' | 'good' | 'needs_work';
    detectedFeatures:  string[];
    viewType?:         'sea' | 'river' | 'open' | 'internal' | 'unknown';
    finishesLevel?:    'basic' | 'standard' | 'premium' | 'luxury';
    notes?:            string;
    analyzedAt:        string;
    imagesAnalyzed:    number;
    costUsd?:          number;
  };

  reservation?: {
    tenantCustomerId?:        string;
    tenantName?:              string;
    tenantPhone?:             string;
    tenantEmail?:             string;
    /** Documento del interesado/comprador (CI por defecto). */
    tenantDocumentType?:      'CI' | 'Pasaporte' | 'Otro';
    tenantDocumentNumber?:    string;
    isShared:                 boolean;
    sharedWithAgentId?:       string;
    sharedWithAgentSnapshot?: { name: string; lastName: string };
    mainAgentSplitPct:        number;
    sharedAgentSplitPct:      number;
    /** Honorarios — el formato depende del tipo de operación.
     *  Alquiler: *Months (meses de alquiler).
     *  Venta:    *Amount (monto absoluto) y/o *Pct (% del precio). */
    ownerChargeMonths?:       number;
    tenantChargeMonths?:      number;
    ownerChargeAmount?:       number;
    ownerChargePct?:          number;
    tenantChargeAmount?:      number;
    tenantChargePct?:         number;
    /** Precio acordado en la reserva (base de honorarios; puede diferir del publicado). */
    agreedPrice?:             number;
    currency:                 CurrencyCode;
    notes?:                   string;
    /** Condiciones comerciales: cómo se paga (contado, financiado, permuta...). */
    paymentMethod?:           string;
    /**
     * Condiciones del negocio de un ALQUILER, cargadas por el agente al reservar.
     * Escribanía las necesita antes de validar. (bug Notario Rentas 31/07)
     */
    dealTerms?: DealTerms;
    /** Escribano de cada parte — contacto suelto, puede ser externo al CRM. */
    buyerNotary?:  NotaryContact;
    sellerNotary?: NotaryContact;
    /** Charla del negocio de la reserva: hilo agente↔notario. */
    thread?: Array<{ author?: string; authorName?: string; role?: string; text: string; createdAt?: string }>;
    /** Datos opcionales para el motor de puntas (Fase 8B). */
    commissionExtras?: {
      otherSideType?: 'imperium_same_office' | 'imperium_other_office' | 'external_agency' | 'none';
      externalAgencyName?: string;
      referral?: {
        type:             'inter' | 'intra';
        referrerAgentId:  string;
        referrerRangoPct: number;
      };
      connector?: {
        userId:     string;
        percentage?: number;
        authorization?: { note?: string };
      };
    };
  };
  /**
   * Quién editó la reserva y qué cambió. Append-only y fuera de `reservation`: ese objeto
   * se reemplaza entero en cada guardado y desaparece al des-reservar.
   */
  reservationHistory?: Array<{
    changedBy?:     string;
    changedByName?: string;
    changedAt?:     string;
    source?:        'crm' | 'gustavo';
    action?:        'created' | 'updated' | 'cancelled';
    changes?:       Array<{ field: string; label: string; from?: string; to?: string }>;
  }>;
  /** Commission auto-generada por el flujo (Fase 8A). */
  commissionId?: string;
  /** Origen de la carga: 'gustavo' si la creó el asistente, 'ui' si la cargó una persona. */
  createdVia?: 'gustavo' | 'ui' | 'phylax' | 'operacion_compartida';
  /** Propiedad de OTRA inmobiliaria: el agente trajo al comprador. Vale media operación. */
  operacionCompartida?: {
    inmobiliaria:    string;
    agenteColega?:   string;
    telefonoColega?: string;
    avisoUrl?:       string;
    boleto:          { url: string; name?: string };
    pesoVenta:       number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PropertyNote {
  _id: string;
  property: string;
  content: string;
  contentPlain: string;
  isPinned: boolean;
  /** Id del autor. El backend ya lo mandaba; faltaba acá, y por eso la UI no podía
   *  saber de quién era la nota para esconder el botón de eliminar. */
  createdBy: string;
  createdBySnapshot: { name: string; lastName: string; avatar?: string };
  createdAt: string;
  updatedAt: string;
  createdVia?: 'gustavo' | 'ui' | 'phylax';
  aiConversationId?: string;
}

export interface GetPropertiesParams {
  page?: number;
  limit?: number;
  status?: string;
  temperature?: string;
  operation?: string;
  type?: string;
  city?: string;
  /** Departamento (Uruguay): filtra por location.state en la vista corporativa. */
  state?: string;
  minPrice?: number;
  maxPrice?: number;
  /**
   * En qué moneda están `minPrice`/`maxPrice`. Sin esto el backend compara el número contra
   * el precio guardado y mezcla monedas: "hasta 25.000" traía alquileres de U$S 12.500
   * (Juan Sires, 08/09/2026). Con moneda convierte contra la cotización del día.
   */
  currency?: 'UYU' | 'USD';
  minArea?: number;
  maxArea?: number;
  bedrooms?: number;
  /** Comodidades: CSV; la propiedad debe tenerlas todas. (Raul) */
  amenities?: string;
  /** Barrio/zona (matchea neighborhood o city). (Raul) */
  neighborhood?: string;
  office?: string;
  assignedTo?: string;
  ownerId?: string;
  featured?: boolean;
  search?: string;
  /** office_admin only: 'network' devuelve TODAS las propiedades de la red. */
  scope?: 'mine' | 'network';
  /** Comparador: busca por ID (propertyId o legacyId), tolerante a guión/case, global. */
  idSearch?: string;
  /** Rango de fecha de creación (calendario del listado, formato YYYY-MM-DD). */
  createdFrom?: string;
  createdTo?: string;
  /** Segmentación por valor: 'high' (high-ticket) / 'common'. */
  priceTier?: 'high' | 'common';
  /** Exclusividad de la captación: 'true' solo exclusivas / 'false' solo no exclusivas. */
  exclusive?: 'true' | 'false';
  /** Orden server-side: campo del grid + dirección. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Feed tenant-wide (todas las oficinas del tenant), sin importar el rol. */
  allOffices?: boolean;
}

/** Motivo que puede marcar escribanía al escribir en la charla del negocio. */
export type MotivoReserva = 'reserva_incompleta' | 'avance_tramite';

export interface ReservationMessage {
  author?: string; authorName?: string; role?: string; text: string; motivo?: MotivoReserva; createdAt?: string;
  /** Si el autor la editó: cuándo, y los textos anteriores (no se pierden). */
  editedAt?: string;
  previousTexts?: Array<{ text: string; editedAt: string }>;
}
export interface ReservationCommissionSplit { type: string; name?: string | null; amount: number; currency: CurrencyCode }

/** Proceso de venta — 4 etapas con fechas (ISO strings). */
export interface SaleProcess {
  reservedAt?: string | null;
  senia?:  { amount?: number | null; date?: string | null } | null;
  boleto?: { signedAt?: string | null; expiresAt?: string | null } | null;
  firma?:  { pactada?: string | null; real?: string | null } | null;
  firmaHistory?: Array<{ from?: string | null; to?: string | null; changedByName?: string; changedAt?: string; reason?: string }>;
}
/** Body del PATCH de proceso de venta (solo las etapas editables). */
export interface SaleProcessPatch {
  senia?:  { amount?: number | null; date?: string | null } | null;
  boleto?: { signedAt?: string | null; expiresAt?: string | null };
  firma?:  { pactada?: string | null; real?: string | null };
  firmaReason?: string;
}

/** Proceso de ALQUILER. Etapas propias del arrendamiento; el inicio y el vencimiento
 *  del contrato NO están acá: los carga la escribanía al validar. */
export interface RentProcess {
  reservedAt?: string | null;
  senia?:    { amount?: number | null; date?: string | null } | null;
  garantia?: { approvedAt?: string | null; tipo?: string | null } | null;
  contrato?: { signedAt?: string | null } | null;
}
export interface RentProcessPatch {
  senia?:    { amount?: number | null; date?: string | null } | null;
  garantia?: { approvedAt?: string | null; tipo?: string | null } | null;
  contrato?: { signedAt?: string | null } | null;
}

// ─── Comisión: pactado vs cobrado ────────────────────────────────────────────
// El monto que entra no siempre es el pactado (negociaciones, operaciones
// compartidas, bonificaciones). Acá viaja el real, con su historial.

export type CommissionSide = 'seller' | 'buyer';
export type CommissionReasonCode = 'shared_operation' | 'negotiation' | 'bonus' | 'other';

export interface CommissionLine {
  side: CommissionSide;
  /** Cómo se llama la contraparte: Propietario / Comprador / Inquilino. */
  label: string;
  /** De dónde sale el número: "3% del precio de cierre", "1 mes de alquiler". */
  base: string;
  calculated: number;
  final: number;
  currency: CurrencyCode;
  ajustado: boolean;
}

export interface CommissionHistoryItem {
  id: string;
  at: string;
  byName: string;
  byRole: string;
  stage: 'validation' | 'correction';
  side: CommissionSide;
  sideLabel: string;
  calculated: number;
  final: number;
  diff: number;
  diffPct: number;
  currency: CurrencyCode;
  reason: string;
  reasonCode?: CommissionReasonCode;
  /** Cotización vigente al momento de este cambio. */
  fxRate?: number;
}

export interface CommissionSettlement {
  operation: 'sale' | 'rent';
  lados: CommissionLine[];
  totalCalculado: number;
  totalFinal: number;
  currency: CurrencyCode;
  compartida: { agente: string | null; mainPct: number; sharedPct: number } | null;
  /** Ya se acreditó en las wallets: la ventana de corrección se cerró. */
  liquidada: boolean;
  /** Los montos se registran SIN IVA. */
  ivaIncluido: false;
  history: CommissionHistoryItem[];
  /** Permiso propio: `commissions:edit`. Sin él los montos se ven, pero no se tocan. */
  puedeEditar: boolean;
}

/** Lo que se manda al corregir: monto cobrado por lado, con motivo si difiere. */
export interface CommissionAdjustment {
  side: CommissionSide;
  final: number;
  reason?: string;
  reasonCode?: CommissionReasonCode;
}

/**
 * Condiciones del negocio de un alquiler: lo que escribanía necesita para armar el
 * contrato y antes solo existía como prosa en las notas. (bug Notario Rentas 31/07)
 */
export interface DealTerms {
  guaranteeType?:   string;
  guaranteeDetail?: string;
  termMonths?:      number;
  startDate?:       string;
}

/** Opciones de garantía de plaza. "Otra" obliga a aclarar cuál.
 *  Espejo de `GUARANTEE_TYPES` del backend (properties.types.ts), que es la lista por la
 *  que pregunta Gustavo: si se agrega una acá, agregarla allá. */
export const GUARANTEE_TYPES = [
  'ANDA',
  'Porto',
  'Depósito en garantía',
  'Fianza solidaria',
  'Contaduría General de la Nación',
  'Sin garantía',
  'Otra',
] as const;

export interface ReservationSummary {
  commission: { status?: string; currency: CurrencyCode; transactionValue?: number; splits: ReservationCommissionSplit[] } | null;
  thread: ReservationMessage[];
  owner: { name: string | null; phone: string | null } | null;
  agreedPrice: number | null;
  currency: CurrencyCode;
  publishedPrice: number | null;
  saleProcess: SaleProcess | null;
  rentProcess: RentProcess | null;
  /** Garantía, plazo e inicio cargados por el agente al reservar (alquileres). */
  dealTerms: DealTerms | null;
  closing: { closedPrice: number | null; closedAt: string | null } | null;
}

/**
 * Ticket promedio del filtro actual, separado por operación y moneda: promediar
 * dólares con pesos —o alquileres con ventas— da un número que no significa nada.
 * `count` dice sobre cuántas propiedades se calculó cada promedio.
 */
export interface TicketPromedio {
  operation: 'sale' | 'rent';
  currency:  string;
  average:   number;
  count:     number;
}

export interface CupoDePropiedades {
  /** `null` = sin tope (Élite, Black, A medida, Imperium). */
  limit: number | null;
  used: number;
  disponibles: number | null;
  /** Todo plan con tope bloquea al llegar (Pro también desde el 29/10/2026: ya no hay +$1). */
  bloquea: boolean;
}

export const propertiesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * El cupo de propiedades ACTIVAS del plan.
     *
     * ⛔ SE CUELGA DEL TAG `LIST`, NO DE UNO PROPIO. Las doce mutaciones de propiedades
     * invalidan `{ type: 'Property', id: 'LIST' }` y ninguna invalida el tipo pelado, así
     * que un tag propio (`id: 'CUPO'`) no lo invalidaría NUNCA: el contador se quedaría
     * clavado en el número del primer render y diría "te quedan 2" sobre un tope ya
     * lleno — peor que no mostrarlo. Colgándose de `LIST` se actualiza con cada alta,
     * activación o pausa sin tocar las doce.
     */
    cupoDePropiedades: build.query<CupoDePropiedades, void>({
      query: () => '/properties/cupo',
      transformResponse: (r: { data: CupoDePropiedades }) => r.data,
      providesTags: [{ type: 'Property' as const, id: 'LIST' }],
    }),

    getProperties: build.query<{ data: Property[]; pagination: PaginationMeta; ticketPromedio?: TicketPromedio[] }, GetPropertiesParams>({
      query: (params) => ({ url: '/properties', params }),
      transformResponse: (res: { data: Property[]; pagination: PaginationMeta; ticketPromedio?: TicketPromedio[] }) => res,
      providesTags: (result) =>
        result
          ? [...result.data.map(({ _id }) => ({ type: 'Property' as const, id: _id })), { type: 'Property', id: 'LIST' }]
          : [{ type: 'Property', id: 'LIST' }],
    }),

    // Mapa de Cierre — cierres terminales de TODA la red (cross-tenant), anonimizados y sin
    // propertyId. Solo data de referencia para el heatmap de precio/m².
    getClosingMap: build.query<Property[], void>({
      query: () => '/properties/closing-map',
      transformResponse: (res: { data: Property[] }) => res.data,
      providesTags: [{ type: 'Property', id: 'CLOSING_MAP' }],
    }),

    /** Capa "Activas" del Mapa de Cierre — oferta propia del tenant, solo visual. */
    getActiveMap: build.query<Property[], void>({
      query: () => '/properties/active-map',
      transformResponse: (res: { data: Property[] }) => res.data,
      providesTags: [{ type: 'Property', id: 'ACTIVE_MAP' }],
    }),

    getPropertyById: build.query<Property, string>({
      query: (id) => `/properties/${id}`,
      transformResponse: (res: { data: Property }) => res.data,
      providesTags: (result, _e, id) => [
        { type: 'Property' as const, id },
        ...(result?._id && result._id !== id ? [{ type: 'Property' as const, id: result._id }] : []),
      ],
    }),

    // Resumen de la reserva: comisión desglosada + charla del negocio.
    getReservationSummary: build.query<ReservationSummary, string>({
      query: (id) => `/properties/${id}/reservation-summary`,
      transformResponse: (res: { data: ReservationSummary }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property' as const, id: `reserva-${id}` }],
    }),

    addReservationMessage: build.mutation<{ thread: ReservationMessage[] }, { id: string; text: string; motivo?: MotivoReserva }>({
      query: ({ id, text, motivo }) => ({ url: `/properties/${id}/reservation-message`, method: 'POST', body: { text, motivo } }),
      transformResponse: (res: { data: { thread: ReservationMessage[] } }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property' as const, id: `reserva-${id}` }],
    }),

    /** Edita una nota PROPIA de la charla (se identifica por su `createdAt`). */
    editReservationMessage: build.mutation<{ thread: ReservationMessage[] }, { id: string; createdAt: string; text: string }>({
      query: ({ id, createdAt, text }) => ({ url: `/properties/${id}/reservation-message`, method: 'PATCH', body: { createdAt, text } }),
      transformResponse: (res: { data: { thread: ReservationMessage[] } }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property' as const, id: `reserva-${id}` }],
    }),

    // Proceso de venta — marca/actualiza las 4 etapas con fechas.
    updateSaleProcess: build.mutation<{ saleProcess: SaleProcess }, { id: string; body: SaleProcessPatch }>({
      query: ({ id, body }) => ({ url: `/properties/${id}/sale-process`, method: 'PATCH', body }),
      transformResponse: (res: { data: { saleProcess: SaleProcess } }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property' as const, id: `reserva-${id}` }],
    }),

    updateRentProcess: build.mutation<{ rentProcess: RentProcess }, { id: string; body: RentProcessPatch }>({
      query: ({ id, body }) => ({ url: `/properties/${id}/rent-process`, method: 'PATCH', body }),
      transformResponse: (res: { data: { rentProcess: RentProcess } }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property' as const, id: `reserva-${id}` }],
    }),

    /**
     * Comisiones de la operación: calculado sobre lo pactado, cobrado vigente e historial.
     * `basePrice` es el número real que se está por confirmar (precio de cierre o alquiler
     * mensual); sin él el calculado saldría del precio publicado.
     */
    getCommissionSettlement: build.query<CommissionSettlement, { id: string; basePrice?: number }>({
      query: ({ id, basePrice }) => ({
        url: `/properties/${id}/commissions`,
        params: basePrice != null ? { basePrice } : undefined,
      }),
      transformResponse: (res: { data: CommissionSettlement }) => res.data,
      providesTags: (_r, _e, { id }) => [{ type: 'Property' as const, id: `comisiones-${id}` }],
    }),

    /**
     * Corrección del monto cobrado. Exige `commissions:edit`, y solo entra ANTES de que
     * la operación pase a vendida o alquilada: después la plata ya se repartió y el
     * backend responde 409.
     */
    updateCommissionSettlement: build.mutation<
      CommissionSettlement & { eventos: number; recalculada: boolean },
      { id: string; ajustes: CommissionAdjustment[] }
    >({
      query: ({ id, ajustes }) => ({ url: `/properties/${id}/commissions`, method: 'PATCH', body: { ajustes } }),
      transformResponse: (res: { data: CommissionSettlement & { eventos: number; recalculada: boolean } }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Property' as const, id: `comisiones-${id}` },
        { type: 'Property' as const, id: `reserva-${id}` },
      ],
    }),

    // Preview read-only de un comparable de tasación (lectura cross-office, fotos + info).
    getComparablePreview: build.query<Property, string>({
      query: (id) => `/properties/${id}/comparable-preview`,
      transformResponse: (res: { data: Property }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property' as const, id }],
    }),

    // Resumen liviano para la card inline de Gustavo: lectura network-wide
    // (no scopeada por oficina), solo campos seguros para la tarjeta.
    getPropertyCard: build.query<PropertyCardSummary, string>({
      query: (id) => `/properties/${id}/card`,
      transformResponse: (res: { data: PropertyCardSummary }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property' as const, id: `card-${id}` }],
    }),

    createProperty: build.mutation<Property, Partial<Property> & { operation: string; title: string; type: string; location: Property['location']; pricing: Property['pricing'] }>({
      query: (body) => ({ url: '/properties', method: 'POST', body }),
      transformResponse: (res: { data: Property }) => res.data,
      invalidatesTags: [{ type: 'Property', id: 'LIST' }],
    }),

    updateProperty: build.mutation<Property, { id: string; body: Partial<Property> }>({
      query: ({ id, body }) => ({ url: `/properties/${id}`, method: 'PUT', body }),
      transformResponse: (res: { data: Property }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }, { type: 'Property', id: 'LIST' }],
    }),

    updatePropertyStatus: build.mutation<
      void,
      {
        id: string;
        status: string;
        note?: string;
        reservation?: Property['reservation'];
        padron?: string;
        /** Dirección estructurada capturada en el form de reserva → `location.*`. */
        address?: string;
        doorNumber?: string;
        apartmentNumber?: string;
        corner?: string;
        closing?: { closedPrice: number; currency: CurrencyCode; notes?: string };
      }
    >({
      query: ({ id, status, note, reservation, padron, address, doorNumber, apartmentNumber, corner, closing }) => ({
        url: `/properties/${id}/status`,
        method: 'PUT',
        body: { status, note, reservation, padron, address, doorNumber, apartmentNumber, corner, closing },
      }),
      // Las colas de escribanía listan reservadas: si el estado cambia, tienen que refrescarse.
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }, { type: 'Property', id: 'LIST' }, 'SaleCase', 'RentalCase'],
    }),

    setPropertyTemperature: build.mutation<
      { propertyId: string; temperature: 'opportunity' | 'cold' | 'warm' | 'hot' | 'negotiating' | null },
      { id: string; temperature: 'opportunity' | 'cold' | 'warm' | 'hot' | 'negotiating' | null }
    >({
      query: ({ id, temperature }) => ({
        url: `/properties/${id}/temperature`,
        method: 'PUT',
        body: { temperature },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }, { type: 'Property', id: 'LIST' }],
    }),

    // Nota interna (descripción privada, HTML) — endpoint focalizado para editarla
    // desde un modal sin tocar el resto de la ficha (pedido Maria Amaral).
    updatePropertyPrivateDescription: build.mutation<
      { propertyId: string; privateDescription: string },
      { id: string; privateDescription: string }
    >({
      query: ({ id, privateDescription }) => ({
        url: `/properties/${id}/private-description`,
        method: 'PUT',
        body: { privateDescription },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }, { type: 'Property', id: 'LIST' }],
    }),

    // Calendario del alquiler temporal (ver utils/temporada.ts). Reemplaza el calendario entero.
    updateTemporada: build.mutation<{ propertyId: string; temporada: Temporada }, { id: string; temporada: Temporada }>({
      query: ({ id, temporada }) => ({ url: `/properties/${id}/temporada`, method: 'PUT', body: temporada }),
      transformResponse: (r: { data: { propertyId: string; temporada: Temporada } }) => r.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }],
    }),

    // Zonas (barrios/ciudades) reales del inventario — para el autocomplete de pedidos.
    getZones: build.query<string[], void>({
      query: () => '/properties/zones',
      transformResponse: (res: { data: string[] }) => res.data,
    }),

    // ─── Matcheo de propiedades por pedido del cliente (incluye bolsa) ───
    matchInterest: build.mutation<InterestMatch[], { operation?: string; type?: string; minPrice?: number; maxPrice?: number; currency?: 'USD' | 'UYU'; bedrooms?: number; bedroomOptions?: string[]; bathroomOptions?: string[]; garagesMin?: number; minCoveredArea?: number; maxCoveredArea?: number; maxAge?: string; furnished?: boolean; petsAllowed?: boolean; guarantees?: string[]; needsBankFinancing?: boolean; zones?: string[]; lat?: number; lng?: number }>({
      query: (body) => ({ url: '/properties/match-interest', method: 'POST', body }),
      transformResponse: (res: { data: InterestMatch[] }) => res.data,
    }),

    // ─── Co-listing (trabajar en conjunto con otro agente) ───────────────
    setCoListing: build.mutation<Property, { id: string; agentId: string; splitPct: number }>({
      query: ({ id, agentId, splitPct }) => ({
        url: `/properties/${id}/co-listing`,
        method: 'PUT',
        body: { agentId, splitPct },
      }),
      transformResponse: (res: { data: Property }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }, { type: 'Property', id: 'LIST' }],
    }),

    removeCoListing: build.mutation<Property, { id: string }>({
      query: ({ id }) => ({ url: `/properties/${id}/co-listing`, method: 'DELETE' }),
      transformResponse: (res: { data: Property }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }, { type: 'Property', id: 'LIST' }],
    }),

    deleteProperty: build.mutation<void, string>({
      query: (id) => ({ url: `/properties/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Property', id: 'LIST' }],
    }),

    discardPropertyDraft: build.mutation<void, string>({
      query: (id) => ({ url: `/properties/${id}/discard-draft`, method: 'POST', body: {} }),
      invalidatesTags: [{ type: 'Property', id: 'LIST' }],
    }),

    addMedia: build.mutation<Property['media'], { id: string; body: { images?: Array<{ url: string; caption?: string; isMain?: boolean }>; videos?: Array<{ url: string; type: 'upload' | 'youtube' | 'vimeo' }>; virtualTour?: string; floorPlan?: string } }>({
      query: ({ id, body }) => ({ url: `/properties/${id}/media`, method: 'POST', body }),
      transformResponse: (res: { data: Property['media'] }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }],
    }),

    removeMedia: build.mutation<void, { id: string; mediaId: string }>({
      query: ({ id, mediaId }) => ({ url: `/properties/${id}/media/${mediaId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }],
    }),

    reorderImages: build.mutation<PropertyImage[], { id: string; order: Array<{ url: string; order: number }> }>({
      query: ({ id, order }) => ({ url: `/properties/${id}/media/reorder`, method: 'PUT', body: { order } }),
      transformResponse: (res: { data: PropertyImage[] }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }],
    }),

    /**
     * Reemplaza UNA foto ya subida — hoy solo el editor de rótulo de portada. `url` va
     * como data URL: el navegador compone el JPG y lo manda entero, igual que en el alta.
     * `overlay: null` saca el texto y deja la foto original.
     */
    updateImage: build.mutation<PropertyImage[], {
      id: string; mediaId: string; url: string; originalUrl?: string; overlay?: PropertyImageOverlay | null;
    }>({
      query: ({ id, mediaId, ...body }) => ({ url: `/properties/${id}/media/${mediaId}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }, { type: 'Property', id: 'LIST' }],
    }),

    setMainImage: build.mutation<PropertyImage[], { id: string; mediaId: string }>({
      query: ({ id, mediaId }) => ({ url: `/properties/${id}/media/${mediaId}/set-main`, method: 'PUT' }),
      transformResponse: (res: { data: PropertyImage[] }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }],
    }),

    // ─── Transferencia / derivación entre agentes ───────────────────────────
    getPropertiesByAgent: build.query<
      {
        agent: { _id: string; name: string; lastName: string; office?: string };
        properties: Property[];
      },
      string
    >({
      query: (agentId) => `/properties/by-agent/${agentId}`,
      transformResponse: (res: { data: { agent: any; properties: Property[] } }) => res.data,
      providesTags: ['Property'],
    }),

    transferProperties: build.mutation<
      {
        transferred: Array<{ _id: string; propertyId: string; title: string; crossOffice: boolean }>;
        skipped:     Array<{ _id: string; reason: string }>;
        toAgent:     { _id: string; name: string; lastName: string; office?: string };
      },
      { propertyIds: string[]; toUserId: string }
    >({
      query: (body) => ({ url: '/properties/transfer', method: 'POST', body }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: ['Property'],
    }),

    toggleFeatured: build.mutation<{ propertyId: string; featured: boolean }, string>({
      query: (id) => ({ url: `/properties/${id}/featured`, method: 'PUT' }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: ['Property'],
    }),

    toggleAiPhotos: build.mutation<{ propertyId: string; aiPhotos: boolean }, string>({
      query: (id) => ({ url: `/properties/${id}/ai-photos`, method: 'PUT' }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: ['Property'],
    }),

    // ─── AVM enrichment ──────────────────────────────────────────────────
    analyzeImages: build.mutation<
      { propertyId: string; imageAnalysis: Property['imageAnalysis'] },
      string
    >({
      query: (propertyId) => ({ url: `/properties/${propertyId}/analyze-images`, method: 'POST' }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: (_r, _e, id) => [{ type: 'Property' as const, id }],
    }),

    enrichGeo: build.mutation<
      { propertyId: string; distances: NonNullable<Property['location']['distances']> },
      string
    >({
      query: (propertyId) => ({ url: `/properties/${propertyId}/enrich-geo`, method: 'POST' }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: (_r, _e, id) => [{ type: 'Property' as const, id }],
    }),

    /** ¿El aviso de MercadoLibre ya está en el CRM, como propiedad o como captación? (extensión) */
    getAvisoImportado: build.query<{
      meliId?: string;
      propiedad: { propertyId: string; title: string; estado: string; agente?: string; esTuya: boolean } | null;
      captacion: { captadaPor: string; agenteId?: string; captacionId?: string; oficina?: string; estado: string } | null;
    }, string>({
      query: (link) => ({ url: '/properties/aviso-importado', params: { link } }),
      transformResponse: (r: { data: never }) => r.data,
    }),
    getPropertyCrmActivity: build.query<PropertyCrmActivity[], string>({
      query: (id) => `/properties/${id}/crm-activity`,
      transformResponse: (res: { data: PropertyCrmActivity[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property' as const, id: `crm-${id}` }],
    }),

    getPropertyHistory: build.query<PropertyHistoryItem[], string>({
      query: (id) => `/properties/${id}/history`,
      transformResponse: (res: { data: PropertyHistoryItem[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property' as const, id: `history-${id}` }],
    }),

    getInterestedCustomers: build.query<InterestedCustomer[], string>({
      query: (id) => `/properties/${id}/interested-customers`,
      transformResponse: (res: { data: InterestedCustomer[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property' as const, id: `interested-${id}` }],
    }),

    // ─── Notas de seguimiento (autor + fecha) ───────────────────────────────
    getPropertyNotes: build.query<PropertyNote[], string>({
      query: (id) => `/properties/${id}/notes`,
      transformResponse: (res: { data: PropertyNote[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property' as const, id: `notes-${id}` }],
    }),

    createPropertyNote: build.mutation<PropertyNote, { propertyId: string; content: string; contentPlain: string; isPinned?: boolean }>({
      query: ({ propertyId, ...body }) => ({ url: `/properties/${propertyId}/notes`, method: 'POST', body }),
      transformResponse: (res: { data: PropertyNote }) => res.data,
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property' as const, id: `notes-${propertyId}` }],
    }),

    updatePropertyNote: build.mutation<PropertyNote, { propertyId: string; noteId: string; body: Partial<PropertyNote> }>({
      query: ({ propertyId, noteId, body }) => ({ url: `/properties/${propertyId}/notes/${noteId}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property' as const, id: `notes-${propertyId}` }],
    }),

    deletePropertyNote: build.mutation<void, { propertyId: string; noteId: string }>({
      query: ({ propertyId, noteId }) => ({ url: `/properties/${propertyId}/notes/${noteId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property' as const, id: `notes-${propertyId}` }],
    }),
  }),
});

export interface InterestedCustomer {
  customer: {
    _id:       string;
    firstName: string;
    lastName:  string;
    phone?:    string;
    email?:    string;
  };
  opportunity: {
    _id:       string;
    stage:     'interested' | 'negotiating' | 'offer_sent';
    value?:    number;
    currency:  CurrencyCode;
    updatedAt: string;
  };
}

export type PropertyHistoryItem =
  | {
      _id: string; kind: 'audit'; date: string; action: string;
      user: { name: string; lastName: string; role: string } | null;
    }
  | {
      _id: string; kind: 'crm'; date: string;
      eventType: 'visit' | 'call' | 'meeting' | 'showing' | 'offer' | 'inquiry';
      status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
      description?: string;
      customer: { firstName: string; lastName: string } | null;
    }
  /**
   * Un cambio de estado de la ficha. `reason` nombra los automáticos: `user_blocked`
   * es la que se bajó porque al agente lo dieron de baja, y es lo que permite
   * distinguirla de una que ya estaba inactiva hace meses.
   */
  | {
      _id: string; kind: 'status'; date: string;
      from: string | null; to: string | null;
      note: string | null;
      reason: 'user_blocked' | 'plan_limit' | 'import' | null;
      user: { name: string; lastName: string; role: string } | null;
    };

export interface PropertyCrmActivity {
  _id:          string;
  type:         'visit' | 'call' | 'meeting' | 'showing' | 'offer' | 'inquiry';
  title:        string;
  date:         string;
  status:       'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  description?: string;
  location?:    string;
  /** Resultado de la visita: 'sale' | 'no_sale'. Vacío = todavía no se cerró. */
  outcome?:     string;
  /** 'phylax' cuando la agendó el Agente Virtual hablando con el cliente. */
  createdVia?:  string;
  /** Quién lo registró, resuelto por el backend. Solo se muestra en los hechos a mano. */
  createdByName?:   string;
  createdByAvatar?: string;
  /** Id del evento en Phylax. Si está, el estado se cambia allá y vuelve por webhook. */
  phylaxEventId?: string;
  /** Resumen de la charla con el Agente que terminó en esta visita. */
  conversationSummary?: string;
  /** Ediciones: qué cambió, quién y por qué. */
  editHistory?: Array<{
    at: string; byName?: string; via?: string; reason?: string;
    changes: Array<{ field: string; from?: string; to?: string }>;
  }>;
  customer:     { _id: string; firstName: string; lastName: string; phone?: string } | null;
}

export const {
  useUpdateTemporadaMutation,
  useGetPropertiesQuery,
  useGetClosingMapQuery,
  useGetActiveMapQuery,
  useGetPropertyByIdQuery,
  useGetReservationSummaryQuery,
  useAddReservationMessageMutation,
  useEditReservationMessageMutation,
  useUpdateSaleProcessMutation,
  useUpdateRentProcessMutation,
  useGetCommissionSettlementQuery,
  useUpdateCommissionSettlementMutation,
  useGetComparablePreviewQuery,
  useGetPropertyCardQuery,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useUpdatePropertyStatusMutation,
  useSetPropertyTemperatureMutation,
  useUpdatePropertyPrivateDescriptionMutation,
  useSetCoListingMutation,
  useRemoveCoListingMutation,
  useMatchInterestMutation,
  useGetZonesQuery,
  useDeletePropertyMutation,
  useDiscardPropertyDraftMutation,
  useAddMediaMutation,
  useRemoveMediaMutation,
  useReorderImagesMutation,
  useSetMainImageMutation,
  useUpdateImageMutation,
  useGetPropertiesByAgentQuery,
  useTransferPropertiesMutation,
  useToggleFeaturedMutation,
  useToggleAiPhotosMutation,
  useAnalyzeImagesMutation,
  useEnrichGeoMutation,
  useGetPropertyCrmActivityQuery,
  useGetAvisoImportadoQuery,
  useGetPropertyHistoryQuery,
  useCupoDePropiedadesQuery,
  useGetInterestedCustomersQuery,
  useGetPropertyNotesQuery,
  useCreatePropertyNoteMutation,
  useUpdatePropertyNoteMutation,
  useDeletePropertyNoteMutation,
} = propertiesApi;
